import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { PagamentoBadge } from '@/components/PixPagamento';
import { 
  Truck, Package, Clock, CheckCircle, XCircle, Calendar, 
  Eye, Check, X, AlertTriangle, Search, Filter,
  UserPlus, Users, Mail, Phone, Power, PowerOff, Pencil, Trash2
} from 'lucide-react';

const statusConfig = {
  pendente: { label: 'Pendente', class: 'bg-amber-100 text-amber-800', icon: Clock },
  confirmada: { label: 'Confirmada', class: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  em_transito: { label: 'Em Trânsito', class: 'bg-purple-100 text-purple-800', icon: Truck },
  concluida: { label: 'Concluída', class: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelada: { label: 'Cancelada', class: 'bg-red-100 text-red-800', icon: XCircle },
};

export default function SolicitacoesPage() {
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog aprovar
  const [showAprovarDialog, setShowAprovarDialog] = useState(false);
  const [selectedSolicitacao, setSelectedSolicitacao] = useState(null);
  const [aprovarForm, setAprovarForm] = useState({
    data_agendada: '',
    hora_agendada: '',
    motorista: '',
    observacoes: '',
  });
  
  // Dialog detalhes
  const [showDetalhesDialog, setShowDetalhesDialog] = useState(false);

  // === GERENCIAMENTO DE USUÁRIOS DO PORTAL ===
  const [showUsuariosDialog, setShowUsuariosDialog] = useState(false);
  const [showNovoUsuarioDialog, setShowNovoUsuarioDialog] = useState(false);
  const [usuariosPortal, setUsuariosPortal] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [novoUsuarioForm, setNovoUsuarioForm] = useState({
    cliente_id: '',
    nome: '',
    email: '',
    telefone: '',
    whatsapp: '',
    senha: '',
  });

  const fetchUsuariosPortal = useCallback(async () => {
    setLoadingUsuarios(true);
    try {
      const [usersRes, clientesRes] = await Promise.all([
        api.get('/portal-cliente/usuarios'),
        api.get('/clientes'),
      ]);
      setUsuariosPortal(usersRes.data);
      setClientes(clientesRes.data);
    } catch {
      toast.error('Erro ao carregar usuários do portal');
    }
    setLoadingUsuarios(false);
  }, []);

  useEffect(() => {
    if (showUsuariosDialog) fetchUsuariosPortal();
  }, [showUsuariosDialog, fetchUsuariosPortal]);

  const handleCriarUsuario = async () => {
    const { cliente_id, nome, email, senha } = novoUsuarioForm;
    if (!cliente_id || !nome || !email || !senha) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    if (senha.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    try {
      await api.post('/portal-cliente/usuarios', novoUsuarioForm);
      toast.success('Usuário criado com sucesso!');
      setShowNovoUsuarioDialog(false);
      setNovoUsuarioForm({ cliente_id: '', nome: '', email: '', telefone: '', whatsapp: '', senha: '' });
      fetchUsuariosPortal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    }
  };

  const handleToggleUsuario = async (usuario) => {
    const novoStatus = !usuario.ativo;
    try {
      await api.put(`/portal-cliente/usuarios/${usuario.id}`, { ativo: novoStatus });
      toast.success(novoStatus ? 'Usuário ativado' : 'Usuário desativado');
      fetchUsuariosPortal();
    } catch {
      toast.error('Erro ao alterar status do usuário');
    }
  };

  // Editar usuário
  const [editUsuario, setEditUsuario] = useState(null);
  const [editForm, setEditForm] = useState({ nome: '', email: '', telefone: '', whatsapp: '' });

  const handleEditarClick = (u) => {
    setEditUsuario(u);
    setEditForm({ nome: u.nome, email: u.email || '', telefone: u.telefone || '', whatsapp: u.whatsapp || '' });
  };

  const handleSalvarEdicao = async () => {
    if (!editForm.nome) { toast.error('Nome é obrigatório'); return; }
    try {
      await api.put(`/portal-cliente/usuarios/${editUsuario.id}`, editForm);
      toast.success('Usuário atualizado');
      setEditUsuario(null);
      fetchUsuariosPortal();
    } catch {
      toast.error('Erro ao atualizar usuário');
    }
  };

  // Excluir usuário
  const [deleteUsuario, setDeleteUsuario] = useState(null);

  const handleExcluirUsuario = async () => {
    try {
      await api.delete(`/portal-cliente/usuarios/${deleteUsuario.id}`);
      toast.success('Usuário excluído');
      setDeleteUsuario(null);
      fetchUsuariosPortal();
    } catch {
      toast.error('Erro ao excluir usuário');
    }
  };

  const fetchSolicitacoes = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterTipo) params.append('tipo', filterTipo);
      
      const res = await api.get(`/portal-cliente/backoffice/solicitacoes?${params}`);
      setSolicitacoes(res.data);
    } catch (error) {
      toast.error('Erro ao carregar solicitações');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterTipo]);

  useEffect(() => {
    fetchSolicitacoes();
  }, [fetchSolicitacoes]);

  const handleAprovar = async () => {
    if (!aprovarForm.data_agendada || !aprovarForm.hora_agendada) {
      toast.error('Defina a data e hora do agendamento');
      return;
    }
    
    try {
      const params = new URLSearchParams({
        data_agendada: aprovarForm.data_agendada,
        hora_agendada: aprovarForm.hora_agendada,
      });
      if (aprovarForm.motorista) params.append('motorista', aprovarForm.motorista);
      if (aprovarForm.observacoes) params.append('observacoes', aprovarForm.observacoes);
      
      await api.post(`/portal-cliente/backoffice/solicitacoes/${selectedSolicitacao.id}/aprovar?${params}`);
      
      toast.success('Solicitação aprovada e agendamento criado!');
      setShowAprovarDialog(false);
      setSelectedSolicitacao(null);
      setAprovarForm({ data_agendada: '', hora_agendada: '', motorista: '', observacoes: '' });
      fetchSolicitacoes();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao aprovar solicitação';
      toast.error(msg);
    }
  };

  const handlePagamento = async (solicitacao, status) => {
    const msg = status === 'pago'
      ? `Confirmar que o Pix de R$ ${Number(solicitacao.pagamento.valor).toFixed(2).replace('.', ',')} da solicitação ${solicitacao.numero} caiu no extrato?\n\nO cliente será avisado no portal (e por WhatsApp, se tiver telefone cadastrado).`
      : 'Voltar o pagamento para pendente?';
    if (!window.confirm(msg)) return;
    try {
      const res = await api.post(`/portal-cliente/backoffice/solicitacoes/${solicitacao.id}/pagamento`, { status });
      toast.success(res.data.message);
      fetchSolicitacoes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar pagamento');
    }
  };

  const handleRejeitar = async (solicitacao) => {
    if (!window.confirm('Tem certeza que deseja rejeitar esta solicitação?')) return;
    
    try {
      await api.put(`/portal-cliente/backoffice/solicitacoes/${solicitacao.id}`, {
        status: 'cancelada'
      });
      toast.success('Solicitação rejeitada');
      fetchSolicitacoes();
    } catch (error) {
      toast.error('Erro ao rejeitar solicitação');
    }
  };

  const openAprovarDialog = (solicitacao) => {
    setSelectedSolicitacao(solicitacao);
    setAprovarForm({
      data_agendada: solicitacao.data_preferencial || '',
      hora_agendada: solicitacao.horario_preferencial || '08:00',
      motorista: '',
      observacoes: '',
    });
    setShowAprovarDialog(true);
  };

  const filteredSolicitacoes = solicitacoes.filter(s => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        s.numero?.toLowerCase().includes(search) ||
        s.cliente_nome?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const pendentes = solicitacoes.filter(s => s.status === 'pendente').length;

  return (
    <div className="space-y-6" data-testid="solicitacoes-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Solicitações de Clientes</h1>
          <p className="text-slate-500">Gerencie as solicitações de coleta e entrega do Portal do Cliente</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setShowUsuariosDialog(true)}
            data-testid="btn-gerenciar-usuarios"
          >
            <Users className="w-4 h-4 mr-2" />
            Usuários do Portal
          </Button>
          {pendentes > 0 && (
            <Badge className="bg-amber-500 text-white px-4 py-2 text-lg">
              <AlertTriangle className="w-5 h-5 mr-2" />
              {pendentes} pendente{pendentes > 1 ? 's' : ''}
            </Badge>
          )}
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Buscar por número ou cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm"
            >
              <option value="">Todos os status</option>
              <option value="pendente">Pendente</option>
              <option value="confirmada">Confirmada</option>
              <option value="em_transito">Em Trânsito</option>
              <option value="concluida">Concluída</option>
              <option value="cancelada">Cancelada</option>
            </select>
            <select
              value={filterTipo}
              onChange={(e) => setFilterTipo(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm"
            >
              <option value="">Todos os tipos</option>
              <option value="coleta">Coleta</option>
              <option value="entrega">Entrega</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Solicitações */}
      <Card>
        <CardContent className="pt-4">
          {loading ? (
            <div className="text-center py-8 text-slate-500">Carregando...</div>
          ) : filteredSolicitacoes.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Nenhuma solicitação encontrada</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredSolicitacoes.map((sol) => {
                const status = statusConfig[sol.status] || statusConfig.pendente;
                const StatusIcon = status.icon;
                const isPendente = sol.status === 'pendente';
                
                return (
                  <div 
                    key={sol.id} 
                    className={`p-4 border rounded-lg ${isPendente ? 'border-amber-300 bg-amber-50' : 'bg-white'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg ${sol.tipo === 'coleta' ? 'bg-blue-100' : 'bg-green-100'}`}>
                          {sol.tipo === 'coleta' ? (
                            <Truck className="w-6 h-6 text-blue-600" />
                          ) : (
                            <Package className="w-6 h-6 text-green-600" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-bold text-lg">{sol.numero}</span>
                            <Badge variant="outline">
                              {sol.tipo === 'coleta' ? 'Coleta' : 'Entrega'}
                            </Badge>
                            {sol.prioridade === 'urgente' && (
                              <Badge className="bg-red-100 text-red-800">Urgente</Badge>
                            )}
                          </div>
                          <p className="font-medium text-slate-700">{sol.cliente_nome}</p>
                          <div className="text-sm text-slate-500 mt-1 space-y-1">
                            <p>
                              <Calendar className="w-4 h-4 inline mr-1" />
                              Data preferencial: {sol.data_preferencial} {sol.horario_preferencial}
                            </p>
                            {sol.itens?.length > 0 && (
                              <p>{sol.itens.length} item(s): {sol.itens.map(i => i.descricao).join(', ')}</p>
                            )}
                            {sol.observacoes && (
                              <p className="italic text-slate-400">"{sol.observacoes}"</p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-end gap-2">
                        <Badge className={status.class}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {status.label}
                        </Badge>

                        {sol.pagamento && (
                          <div className="flex items-center gap-2">
                            <PagamentoBadge pagamento={sol.pagamento} testId={`bo-pagamento-status-${sol.id}`} />
                            {sol.pagamento.status !== 'pago' ? (
                              <Button size="sm" variant="outline" className="h-7 text-emerald-700 border-emerald-300 hover:bg-emerald-50" onClick={() => handlePagamento(sol, 'pago')} data-testid={`btn-confirmar-pagamento-${sol.id}`}>
                                <Check className="w-3.5 h-3.5 mr-1" /> Confirmar Pix
                              </Button>
                            ) : (
                              <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => handlePagamento(sol, 'pendente')} title={`Confirmado por ${sol.pagamento.confirmado_por || '-'}`} data-testid={`btn-desfazer-pagamento-${sol.id}`}>
                                Desfazer
                              </Button>
                            )}
                          </div>
                        )}
                        
                        {isPendente && (
                          <div className="flex gap-2 mt-2">
                            <Button
                              size="sm"
                              onClick={() => openAprovarDialog(sol)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Aprovar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRejeitar(sol)}
                              className="text-red-600 border-red-300 hover:bg-red-50"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Rejeitar
                            </Button>
                          </div>
                        )}
                        
                        {sol.status === 'confirmada' && sol.agendamento_id && (
                          <p className="text-xs text-blue-600">
                            Agendado: {sol.data_confirmada} às {sol.horario_confirmado}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Aprovar */}
      <Dialog open={showAprovarDialog} onOpenChange={setShowAprovarDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Aprovar Solicitação
            </DialogTitle>
          </DialogHeader>
          
          {selectedSolicitacao && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium">{selectedSolicitacao.numero}</p>
                <p className="text-sm text-slate-600">
                  {selectedSolicitacao.cliente_nome} - {selectedSolicitacao.tipo === 'coleta' ? 'Coleta' : 'Entrega'}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Data do Agendamento *</Label>
                  <Input
                    type="date"
                    value={aprovarForm.data_agendada}
                    onChange={(e) => setAprovarForm(f => ({...f, data_agendada: e.target.value}))}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <Label>Horário *</Label>
                  <Input
                    type="time"
                    value={aprovarForm.hora_agendada}
                    onChange={(e) => setAprovarForm(f => ({...f, hora_agendada: e.target.value}))}
                  />
                </div>
              </div>
              
              <div>
                <Label>Motorista/Responsável</Label>
                <Input
                  value={aprovarForm.motorista}
                  onChange={(e) => setAprovarForm(f => ({...f, motorista: e.target.value}))}
                  placeholder="Nome do motorista"
                />
              </div>
              
              <div>
                <Label>Observações internas</Label>
                <Textarea
                  value={aprovarForm.observacoes}
                  onChange={(e) => setAprovarForm(f => ({...f, observacoes: e.target.value}))}
                  placeholder="Observações para a equipe..."
                  rows={2}
                />
              </div>
              
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
                <p className="font-medium">Ao aprovar:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Um agendamento será criado automaticamente</li>
                  <li>O cliente será notificado via WhatsApp/Email</li>
                  <li>A solicitação aparecerá na tela de Agendamentos</li>
                </ul>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAprovarDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAprovar} className="bg-green-600 hover:bg-green-700">
              <Check className="w-4 h-4 mr-2" />
              Aprovar e Agendar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog - Usuários do Portal */}
      <Dialog open={showUsuariosDialog} onOpenChange={setShowUsuariosDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Usuários do Portal do Cliente
            </DialogTitle>
            <DialogDescription>
              Gerencie os usuários que acessam o Portal do Cliente
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Button
              onClick={() => setShowNovoUsuarioDialog(true)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="btn-novo-usuario-portal"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Novo Usuário
            </Button>

            {loadingUsuarios ? (
              <div className="text-center py-8 text-slate-400">Carregando...</div>
            ) : usuariosPortal.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>Nenhum usuário cadastrado</p>
              </div>
            ) : (
              <div className="space-y-3">
                {usuariosPortal.map(u => (
                  <div key={u.id} className={`flex items-center justify-between p-4 rounded-lg border ${u.ativo ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200 opacity-60'}`} data-testid={`usuario-portal-${u.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{u.nome}</span>
                        <Badge className={u.ativo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {u.ativo ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" />
                          {u.email}
                        </span>
                        {u.telefone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5" />
                            {u.telefone}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        Cliente: {u.cliente_nome || 'N/A'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditarClick(u)}
                        title="Editar usuário"
                        data-testid={`edit-usuario-${u.id}`}
                      >
                        <Pencil className="w-4 h-4 text-blue-500" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleUsuario(u)}
                        title={u.ativo ? 'Desativar usuário' : 'Ativar usuário'}
                        data-testid={`toggle-usuario-${u.id}`}
                      >
                        {u.ativo ? <PowerOff className="w-4 h-4 text-amber-500" /> : <Power className="w-4 h-4 text-green-500" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteUsuario(u)}
                        title="Excluir usuário"
                        data-testid={`delete-usuario-${u.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog - Novo Usuário do Portal */}
      <Dialog open={showNovoUsuarioDialog} onOpenChange={setShowNovoUsuarioDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Novo Usuário do Portal
            </DialogTitle>
            <DialogDescription>
              Crie um usuário para um cliente acessar o Portal
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Cliente *</Label>
              <Select
                value={novoUsuarioForm.cliente_id || undefined}
                onValueChange={(v) => setNovoUsuarioForm(f => ({...f, cliente_id: v}))}
              >
                <SelectTrigger data-testid="select-cliente">
                  <SelectValue placeholder="Selecione o cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clientes.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Nome completo *</Label>
              <Input
                value={novoUsuarioForm.nome}
                onChange={(e) => setNovoUsuarioForm(f => ({...f, nome: e.target.value}))}
                placeholder="Nome do usuário"
                data-testid="input-nome-usuario"
              />
            </div>
            
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={novoUsuarioForm.email}
                onChange={(e) => setNovoUsuarioForm(f => ({...f, email: e.target.value}))}
                placeholder="email@exemplo.com"
                data-testid="input-email-usuario"
              />
            </div>
            
            <div>
              <Label>Senha *</Label>
              <Input
                type="password"
                value={novoUsuarioForm.senha}
                onChange={(e) => setNovoUsuarioForm(f => ({...f, senha: e.target.value}))}
                placeholder="Mínimo 6 caracteres"
                data-testid="input-senha-usuario"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Telefone</Label>
                <Input
                  value={novoUsuarioForm.telefone}
                  onChange={(e) => setNovoUsuarioForm(f => ({...f, telefone: e.target.value}))}
                  placeholder="(00) 0000-0000"
                  data-testid="input-telefone-usuario"
                />
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input
                  value={novoUsuarioForm.whatsapp}
                  onChange={(e) => setNovoUsuarioForm(f => ({...f, whatsapp: e.target.value}))}
                  placeholder="(00) 00000-0000"
                  data-testid="input-whatsapp-usuario"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovoUsuarioDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCriarUsuario} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-salvar-usuario">
              <UserPlus className="w-4 h-4 mr-2" />
              Criar Usuário
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog - Editar Usuário do Portal */}
      <Dialog open={!!editUsuario} onOpenChange={(v) => { if (!v) setEditUsuario(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Editar Usuário
            </DialogTitle>
            <DialogDescription>
              Editando: {editUsuario?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome completo *</Label>
              <Input
                value={editForm.nome}
                onChange={(e) => setEditForm(f => ({...f, nome: e.target.value}))}
                data-testid="edit-input-nome"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm(f => ({...f, email: e.target.value}))}
                data-testid="edit-input-email"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Telefone</Label>
                <Input
                  value={editForm.telefone}
                  onChange={(e) => setEditForm(f => ({...f, telefone: e.target.value}))}
                  data-testid="edit-input-telefone"
                />
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input
                  value={editForm.whatsapp}
                  onChange={(e) => setEditForm(f => ({...f, whatsapp: e.target.value}))}
                  data-testid="edit-input-whatsapp"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUsuario(null)}>Cancelar</Button>
            <Button onClick={handleSalvarEdicao} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-salvar-edicao">
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog - Confirmar Exclusão */}
      <Dialog open={!!deleteUsuario} onOpenChange={(v) => { if (!v) setDeleteUsuario(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Excluir Usuário
            </DialogTitle>
            <DialogDescription>
              Esta ação é irreversível. O usuário perderá o acesso ao portal.
            </DialogDescription>
          </DialogHeader>
          {deleteUsuario && (
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="font-medium text-slate-900">{deleteUsuario.nome}</p>
              <p className="text-sm text-slate-500">{deleteUsuario.email}</p>
              <p className="text-xs text-slate-400">Cliente: {deleteUsuario.cliente_nome}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUsuario(null)}>Cancelar</Button>
            <Button onClick={handleExcluirUsuario} variant="destructive" data-testid="btn-confirmar-exclusao">
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
